"use client";

import * as React from "react";

import { cn } from "../cn";

// Parity P1 (hybrid design D1, §3.1), TASK-DRAWER-CHAT: the task's host layout, decided by the
// HOST's width (the drawer, the full page's main region), never the viewport's. Two panes: the
// work (head + body) and the rail (the conversation, with the properties and the activity as its
// other tabs). The rail gets half the width by default; a handle between the panes moves the
// split (0.3–0.7 of the width, within each pane's minimum), and the host remembers it.
// - `split`: work | rail side by side, each scrolling on its own, so the rail's composer stays
//   at the bottom (the rail can be folded to a strip);
// - `stacked`: one scrolling column, the rail's content under the work.

export const SPLIT_PANES = { work: 340, rail: 300 } as const;

export type SplitMode = "split" | "stacked";

export function splitModeFor(width: number): SplitMode {
  return width >= SPLIT_PANES.work + SPLIT_PANES.rail ? "split" : "stacked";
}

/** The element's content-box width, measured before paint and kept current. */
export function useContainerWidth<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  initial = 0,
): number {
  const [width, setWidth] = React.useState(initial);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

export type SplitRailProps = Omit<React.ComponentProps<"div">, "children"> & {
  /** Title, chips, core row: the top of the work pane. */
  head: React.ReactNode;
  /** The work pane's body (sections). */
  work: React.ReactNode;
  rail: React.ReactNode;
  /** In `split`, whether the rail column is open (folded: a strip with `railToggle`). */
  railOpen: boolean;
  /** The rail's open/fold control, shown in the folded strip and at the rail's top right. */
  railToggle?: React.ReactNode;
  /** Forces a mode (tests); otherwise measured. */
  mode?: SplitMode;
  /** A first guess of the host width (avoids a flash before the first measure). */
  initialWidth?: number;
  railLabel?: string;
  /** The rail's share of the width in `split` (0.3–0.7, default half). */
  railShare?: number;
  /** A drag (or arrow keys) on the handle between the panes ended on this share. */
  onRailShareChange?: (share: number) => void;
};

export const RAIL_SHARE = { min: 0.3, max: 0.7, default: 0.5, step: 0.05 } as const;

/** The share, kept inside 0.3–0.7 and inside both panes' minimums for this width. */
export function clampRailShare(share: number, width: number): number {
  const lo = Math.max(RAIL_SHARE.min, width > 0 ? SPLIT_PANES.rail / width : 0);
  const hi = Math.min(RAIL_SHARE.max, width > 0 ? 1 - SPLIT_PANES.work / width : 1);
  return Math.min(Math.max(share, lo), Math.max(lo, hi));
}

/**
 * The host layout. It fills its flex-column parent (`min-h-0 flex-1`). `data-mode` says which
 * arrangement is on screen, and the host is a `group/split`, so the rail's content can fill the
 * column in `split` (`group-data-[mode=split]/split:`). The rail mounts once whichever way it is
 * laid out (switching modes or folding never refetches it or loses a draft).
 */
export function SplitRail({
  head,
  work,
  rail,
  railOpen,
  railToggle,
  mode: forced,
  initialWidth = 0,
  railLabel = "Conversation",
  railShare = RAIL_SHARE.default,
  onRailShareChange,
  className,
  ...rest
}: SplitRailProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const width = useContainerWidth(ref, initialWidth);
  const mode = forced ?? splitModeFor(width);
  const split = mode === "split";
  const folded = split && !railOpen;
  // While dragging the share is local; the end of the drag reports it once.
  const [live, setLive] = React.useState<number | null>(null);
  const share = clampRailShare(live ?? railShare, width);
  const columns = split
    ? railOpen
      ? // In percent-sized fr (each ≥ 1): a flex sum under 1 would leave space unused once a
        // pane sits at its minimum.
        `minmax(${SPLIT_PANES.work}px,${((1 - share) * 100).toFixed(1)}fr) minmax(${SPLIT_PANES.rail}px,${(share * 100).toFixed(1)}fr)`
      : "minmax(0,1fr) 40px"
    : "minmax(0,1fr)";
  const shareAt = (clientX: number) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || box.width <= 0) return share;
    return clampRailShare((box.right - clientX) / box.width, box.width);
  };
  const commit = (next: number) => {
    setLive(null);
    const v = Math.round(next * 1000) / 1000;
    if (v !== railShare) onRailShareChange?.(v);
  };
  const handle = (
    // biome-ignore lint/a11y/useSemanticElements: a focusable splitter (WAI-ARIA window splitter), not an <hr>.
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the conversation"
      aria-valuemin={RAIL_SHARE.min * 100}
      aria-valuemax={RAIL_SHARE.max * 100}
      aria-valuenow={Math.round(share * 100)}
      tabIndex={0}
      data-slot="split-handle"
      title="Drag to resize (double-click: half and half)"
      className="group/handle absolute top-0 bottom-0 -left-1.5 z-20 flex w-3 cursor-col-resize touch-none justify-center outline-none"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setLive(shareAt(e.clientX));
      }}
      onPointerMove={(e) => {
        if (live === null) return;
        setLive(shareAt(e.clientX));
      }}
      onPointerUp={(e) => {
        if (live === null) return;
        commit(shareAt(e.clientX));
      }}
      onPointerCancel={() => setLive(null)}
      onDoubleClick={() => commit(clampRailShare(RAIL_SHARE.default, width))}
      onKeyDown={(e) => {
        const d =
          e.key === "ArrowLeft" ? RAIL_SHARE.step : e.key === "ArrowRight" ? -RAIL_SHARE.step : 0;
        if (!d) return;
        e.preventDefault();
        commit(clampRailShare(share + d, width));
      }}
    >
      <span
        aria-hidden="true"
        className={cn(
          "h-full w-0.5 bg-transparent transition-colors group-hover/handle:bg-ring group-focus-visible/handle:bg-ring",
          live !== null && "bg-ring",
        )}
      />
    </div>
  );
  return (
    <div
      ref={ref}
      data-slot="split-host"
      data-mode={mode}
      className={cn(
        "group/split grid min-h-0 flex-1",
        split ? "grid-rows-[minmax(0,1fr)] overflow-hidden" : "content-start overflow-y-auto",
        className,
      )}
      style={{ gridTemplateColumns: columns }}
      {...rest}
    >
      <div
        data-slot="split-work"
        className={cn("col-start-1 row-start-1 min-w-0", split && "min-h-0 overflow-y-auto")}
      >
        <div className="px-6 pt-5 max-md:px-4">{head}</div>
        <div className="px-6 pb-4 max-md:px-4">{work}</div>
      </div>
      {folded ? (
        <div
          data-slot="split-rail-folded"
          className="col-start-2 row-start-1 flex flex-col items-center border-l border-subtle py-2"
        >
          {railToggle}
        </div>
      ) : null}
      <section
        aria-label={railLabel}
        data-slot="split-rail"
        hidden={folded}
        className={cn(
          "relative min-w-0",
          split
            ? "col-start-2 row-start-1 flex min-h-0 flex-col border-l border-subtle"
            : "col-start-1 mt-4 px-6 pb-6 max-md:px-4",
        )}
      >
        {split && !folded ? handle : null}
        {split && railToggle ? (
          <div className="absolute top-1 right-2 z-10">{railToggle}</div>
        ) : null}
        <div className={cn(split && "flex min-h-0 flex-1 flex-col px-4 pt-1 pb-3")}>{rail}</div>
      </section>
    </div>
  );
}
